import { storableError } from '../../util/errors';
import { fetchSkillFitCandidateHold, fetchSkillFitInterviewGateStatus } from '../../util/api';

// ================ Action types ================ //

export const FETCH_GATE_STATUS_REQUEST = 'app/SkillFitInterviewGatePage/FETCH_GATE_STATUS_REQUEST';
export const FETCH_GATE_STATUS_SUCCESS = 'app/SkillFitInterviewGatePage/FETCH_GATE_STATUS_SUCCESS';
export const FETCH_GATE_STATUS_ERROR = 'app/SkillFitInterviewGatePage/FETCH_GATE_STATUS_ERROR';

// The held candidate's own 5-second tick.
export const CANDIDATE_HOLD_SUCCESS = 'app/SkillFitInterviewGatePage/CANDIDATE_HOLD_SUCCESS';
export const CANDIDATE_HOLD_ERROR = 'app/SkillFitInterviewGatePage/CANDIDATE_HOLD_ERROR';

// ================ Reducer ================ //

const initialState = {
  fetchInProgress: false,
  fetchError: null,
  gateStatus: null,
  /*
   * The last hold answer, stored WHOLE — `{ held, holdReason, status, proctorPresent,
   * holdExpiresAt, holdExpired }`, the same four hold fields the join body carries, so the hold
   * screen reads one shape whichever call filled it.
   *
   * ⛔ No `holdPollInProgress` key. The poll runs on a timer behind a screen that is already
   * explicitly a waiting screen; a spinner that blinks every five seconds would report the
   * transport, not the wait, and the candidate has no action to take on either.
   */
  hold: null,
  // ⛔ A failed tick is NOT a refusal and must never replace the hold screen — it is the absence of
  // an answer, and the next tick usually has one. Rendered as a quiet transient line, exactly as
  // the proctor's waiting screen treats its own poll gaps.
  holdPollError: null,
};

export default function skillFitInterviewGatePageReducer(state = initialState, action = {}) {
  const { type, payload } = action;
  switch (type) {
    case FETCH_GATE_STATUS_REQUEST:
      return { ...state, fetchInProgress: true, fetchError: null };
    case FETCH_GATE_STATUS_SUCCESS:
      return { ...state, fetchInProgress: false, gateStatus: payload };
    case FETCH_GATE_STATUS_ERROR:
      return { ...state, fetchInProgress: false, fetchError: payload };
    case CANDIDATE_HOLD_SUCCESS:
      // The error is cleared on any answer at all: a tick that lands means the gap is over,
      // whatever the answer says about the hold itself.
      return { ...state, hold: payload || null, holdPollError: null };
    case CANDIDATE_HOLD_ERROR:
      // ⛔ `hold` is deliberately left standing. It carries `holdExpiresAt` — the only thing on the
      // screen that tells the candidate how long this lasts — and a dropped tick is no reason to
      // take that back.
      return { ...state, holdPollError: payload };
    default:
      return state;
  }
}

// ================ Action creators ================ //

export const fetchGateStatusRequest = () => ({ type: FETCH_GATE_STATUS_REQUEST });
export const fetchGateStatusSuccess = payload => ({ type: FETCH_GATE_STATUS_SUCCESS, payload });
export const fetchGateStatusError = e => ({ type: FETCH_GATE_STATUS_ERROR, error: true, payload: e });
export const candidateHoldSuccess = payload => ({ type: CANDIDATE_HOLD_SUCCESS, payload });
export const candidateHoldError = e => ({ type: CANDIDATE_HOLD_ERROR, error: true, payload: e });

// ================ Selectors ================ //

const slice = state => (state && state.SkillFitInterviewGatePage) || {};

export const selectCandidateHold = state => slice(state).hold || null;
export const selectCandidateHoldPollError = state => slice(state).holdPollError || null;

/**
 * ⭐ The release — the single predicate the hold screen's one remaining join turns on.
 *
 * ⛔ It requires a POSITIVE `held: false` from a tick that actually landed, and is deliberately not
 * written as `!isHeld`. The hold screen is only ever reached because a join already answered
 * `held: true`, so absence of an answer means the poll has not landed yet, NOT that the recruiter
 * arrived. Reading a missing value as a release would send the candidate into a join the entry gate
 * holds all over again — a loop they cannot escape, off a single dropped tick.
 */
export const selectHoldReleased = state => {
  const hold = selectCandidateHold(state);
  return !!hold && hold.held === false;
};

/**
 * Clock A ran out. ⛔ NOT a failure and never rendered as one: nothing is recorded,
 * no credit is consumed and the link stays live — the candidate simply returns.
 */
export const selectHoldExpired = state => {
  const hold = selectCandidateHold(state);
  return !!(hold && hold.holdExpired);
};

// ================ Thunks ================ //

// Re-fetches on EVERY call — never reads/writes a cached value — so the Integrity Check gate is
// always evaluated against current status (Requirement: "Live, entry-time Integrity Check status
// re-check", including a revisit of the same link after completing Integrity Check).
export const loadData = params => dispatch => {
  const token = params && params.token;
  dispatch(fetchGateStatusRequest());
  return fetchSkillFitInterviewGateStatus(token)
    .then(response => {
      dispatch(fetchGateStatusSuccess(response));
      return response;
    })
    .catch(e => {
      dispatch(fetchGateStatusError(storableError(e)));
      throw e;
    });
};

/**
 * "Is my interviewer here yet?", the held candidate's 5-second tick.
 *
 * ⛔ **This is what the hold screen polls, and `joinInterviewRoom` is NOT.** That call's handoff
 * token is single-use, so polling the join would burn a fresh redemption every tick and mint a
 * participant token discarded on all but the last. Join once, poll this, join once more when the
 * release lands.
 *
 * ⚠ It is also the candidate's presence heartbeat, and Clock A's ONLY writer — the server records
 * `firstSeenAt` on this call. Stop ticking and the candidate's own 15-minute bound stops being
 * written, and the recruiter's arrival notice stops naming a release time.
 *
 * ⭐ **Resolves, never rejects.** The caller is a `setInterval`, which has nowhere to put a rejection
 * and would leave an unhandled one on every tick of an outage; the failure is fully represented in
 * `holdPollError`, and the screen's next action is the same either way — keep waiting.
 */
export const pollCandidateHold = interviewToken => dispatch => {
  return fetchSkillFitCandidateHold({ interviewToken })
    .then(response => {
      dispatch(candidateHoldSuccess(response));
      return response;
    })
    .catch(e => {
      dispatch(candidateHoldError(storableError(e)));
      return null;
    });
};
